/**
 * Agent Actions Toolbar Component
 *
 * Displays action toolbar for agent drawer, providing:
 * - Share button (copy drawer URL to clipboard)
 * - Add Credential button (Praetorian only)
 * - Add User button (Praetorian only)
 * - Provision Tunnel button (Praetorian only)
 * - Export button (optional, for future use)
 *
 * Follows the platform pattern for drawer action bars with role-based access control.
 */

import { CloudIcon, KeyIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import type { ActionConfigUnion } from '@/components/table/actions';
import {
  buildExportAction,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import type { Agent } from '@/hooks/useAgents';

/**
 * Props for AgentActions component
 */
export interface AgentActionsProps {
  /** Agent to perform actions on */
  agent: Agent | null;
  /** Handler for opening export modal (optional) */
  onExportClick?: () => void;
  /** Handler for opening Add Credential modal (Praetorian only) */
  onAddCredentialClick?: () => void;
  /** Handler for opening Add User modal (Praetorian only) */
  onAddUserClick?: () => void;
  /** Handler for opening Provision Tunnel modal (Praetorian only) */
  onProvisionTunnelClick?: () => void;
  /** Whether current user is Praetorian (controls visibility of management actions) */
  isPraetorianUser?: boolean;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Action toolbar component for agent drawer operations
 *
 * Provides configuration-based actions:
 * - Share: Copy drawer URL to clipboard
 * - Export: (optional) Open export modal
 *
 * @example
 * ```tsx
 * <AgentActions
 *   agent={displayAgent}
 *   hideLabel
 * />
 * ```
 */
export function AgentActions({
  agent,
  onExportClick,
  onAddCredentialClick,
  onAddUserClick,
  onProvisionTunnelClick,
  isPraetorianUser = false,
  hideLabel,
  className,
}: AgentActionsProps) {
  // Default share handler: Copy current URL to clipboard
  const handleShare = async () => {
    try {
      // Guard for environments where clipboard API may not be available
      // (non-HTTPS, SSR, some test environments, or unsupported browsers)
      if (!navigator?.clipboard?.writeText) {
        toast.error('Clipboard not available');
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Build action configurations in alphabetical order
  const actions: ActionConfigUnion[] = [];

  // Add Praetorian-only management actions - wrapped in single render to avoid type collision
  // TableActionsBar stores components by type name, so multiple actions with same type overwrite each other
  if (isPraetorianUser) {
    actions.push({
      type: 'Change Status' as const,
      render: () => (
        <div className="flex items-center gap-2">
          <Button
            styleType="rounded"
            size="xxs"
            label="Add Credential"
            startIcon={<KeyIcon className="size-4" />}
            onClick={onAddCredentialClick}
            data-testid="agent-add-credential-button"
            aria-label="Add Active Directory credential"
          />
          <Button
            styleType="rounded"
            size="xxs"
            label="Add User"
            startIcon={<UserPlusIcon className="size-4" />}
            onClick={onAddUserClick}
            data-testid="agent-add-user-button"
            aria-label="Add Linux user"
          />
          <Button
            styleType="rounded"
            size="xxs"
            label="Provision Tunnel"
            startIcon={<CloudIcon className="size-4" />}
            onClick={onProvisionTunnelClick}
            data-testid="agent-provision-tunnel-button"
            aria-label="Provision Cloudflare tunnel"
          />
        </div>
      ),
    });
  }

  // Share action (S) - always visible, comes after management actions alphabetically
  actions.push(
    buildShareAction({
      variant: 'button-only',
      onShareClick: handleShare,
      testId: 'agent-share-button',
    })
  );

  // Add export action if handler provided
  if (onExportClick) {
    actions.push(
      buildExportAction({
        onExportClick,
        disabled: false,
        testId: 'agent-export-button',
      })
    );
  }

  return (
    <TableActionsBar
      selectedItems={agent ? [agent] : []}
      actions={actions}
      disabled={!agent}
      hideLabel={hideLabel}
      className={className}
      testIdPrefix="agent"
    />
  );
}
